import { useState, useEffect, useCallback, useMemo } from 'react';
import { instrumentAPI } from '../../../../../services';
import { 
  Clock, 
  Calendar, 
  Globe, 
  Download, 
  Upload, 
  Plus, 
  Search, 
  Filter,
  ChevronRight,
  ChevronDown,
  Trash2,
  Edit3,
  X,
  Check,
  AlertCircle,
  Loader2,
  CalendarDays,
  CalendarClock,
  Layers,
  FileJson,
  Sparkles,
  ArrowLeft,
  Eye,
  EyeOff,
  Copy,
  MoreHorizontal
} from 'lucide-react';
import './PresetManager.css';

const TIMEZONES = [
  { value: 'UTC', label: 'UTC', offset: '+00:00' },
  { value: 'America/New_York', label: 'New York', offset: '-05:00' },
  { value: 'America/Chicago', label: 'Chicago', offset: '-06:00' },
  { value: 'America/Los_Angeles', label: 'Los Angeles', offset: '-08:00' },
  { value: 'Europe/London', label: 'London', offset: '+00:00' },
  { value: 'Europe/Paris', label: 'Paris', offset: '+01:00' },
  { value: 'Europe/Berlin', label: 'Berlin', offset: '+01:00' },
  { value: 'Asia/Tokyo', label: 'Tokyo', offset: '+09:00' },
  { value: 'Asia/Shanghai', label: 'Shanghai', offset: '+08:00' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong', offset: '+08:00' },
  { value: 'Asia/Singapore', label: 'Singapore', offset: '+08:00' },
  { value: 'Australia/Sydney', label: 'Sydney', offset: '+11:00' }
];

const DAYS_OF_WEEK = [
  { key: 'monday', label: 'Monday', short: 'Mon' },
  { key: 'tuesday', label: 'Tuesday', short: 'Tue' },
  { key: 'wednesday', label: 'Wednesday', short: 'Wed' },
  { key: 'thursday', label: 'Thursday', short: 'Thu' },
  { key: 'friday', label: 'Friday', short: 'Fri' },
  { key: 'saturday', label: 'Saturday', short: 'Sat' },
  { key: 'sunday', label: 'Sunday', short: 'Sun' }
];

const PRESET_CATEGORIES = [
  { value: 'forex', label: 'Forex', icon: Layers, color: '#10b981', bgColor: 'rgba(16, 185, 129, 0.1)' },
  { value: 'stocks', label: 'Stocks', icon: Layers, color: '#3b82f6', bgColor: 'rgba(59, 130, 246, 0.1)' },
  { value: 'crypto', label: 'Crypto', icon: Layers, color: '#f59e0b', bgColor: 'rgba(245, 158, 11, 0.1)' },
  { value: 'commodities', label: 'Commodities', icon: Layers, color: '#8b5cf6', bgColor: 'rgba(139, 92, 246, 0.1)' },
  { value: 'indices', label: 'Indices', icon: Layers, color: '#ef4444', bgColor: 'rgba(239, 68, 68, 0.1)' },
  { value: 'custom', label: 'Custom', icon: Layers, color: '#64748b', bgColor: 'rgba(100, 116, 139, 0.1)' }
];

const HOLIDAY_TYPES = [
  { value: 'full_closure', label: 'Full Closure', icon: X },
  { value: 'early_closure', label: 'Early Closure', icon: Clock },
  { value: 'late_opening', label: 'Late Opening', icon: CalendarClock }
];

const QUICK_PRESETS = [
  {
    name: '24/7 Crypto',
    description: 'Always open for cryptocurrency markets',
    category: 'crypto',
    trading_hours: {
      timezone: 'UTC',
      weekly_schedule: DAYS_OF_WEEK.map(day => ({
        day: day.key,
        sessions: [{ open: '00:00', close: '23:59', name: 'Trading' }]
      })),
      holidays: []
    }
  },
  {
    name: 'Forex Standard',
    description: 'Standard forex market hours (Mon-Fri)',
    category: 'forex',
    trading_hours: {
      timezone: 'UTC',
      weekly_schedule: DAYS_OF_WEEK.slice(0, 5).map(day => ({
        day: day.key,
        sessions: [{ open: '00:00', close: '24:00', name: 'Trading' }]
      })),
      holidays: []
    }
  },
  {
    name: 'US Stocks',
    description: 'US equity market hours (9:30 AM - 4:00 PM ET)',
    category: 'stocks',
    trading_hours: {
      timezone: 'America/New_York',
      weekly_schedule: DAYS_OF_WEEK.slice(0, 5).map(day => ({
        day: day.key,
        sessions: [{ open: '09:30', close: '16:00', name: 'Regular Trading' }]
      })),
      holidays: []
    }
  }
];

function PresetManager({ onClose }) {
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('list');
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [expandedDays, setExpandedDays] = useState({});
  const [selectedDayForEdit, setSelectedDayForEdit] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  let importInputRef = null;
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'forex',
    trading_hours: {
      timezone: 'UTC',
      weekly_schedule: [],
      holidays: []
    }
  });

  useEffect(() => {
    fetchPresets();
  }, []);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), toast.duration || 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const showToast = useCallback((message, type = 'info', duration = 3000) => {
    setToast({ message, type, duration });
  }, []);

  const fetchPresets = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await instrumentAPI.getTradingHoursPresets();
      if (response.data && response.data.presets) {
        setPresets(response.data.presets);
      } else if (Array.isArray(response.data)) {
        setPresets(response.data);
      } else {
        setPresets([]);
      }
    } catch (err) {
      console.error('Error fetching presets:', err);
      setError('Failed to load presets. Please check your server connection.');
      setPresets([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setFormData({
      name: '',
      description: '',
      category: 'forex',
      trading_hours: {
        timezone: 'UTC',
        weekly_schedule: [],
        holidays: []
      }
    });
    setSelectedPreset(null);
    setActiveTab('create');
  };

  const handleEdit = (preset) => {
    setFormData({
      name: preset.name,
      description: preset.description,
      category: preset.category,
      trading_hours: preset.trading_hours || {
        timezone: 'UTC',
        weekly_schedule: [],
        holidays: []
      }
    });
    setSelectedPreset(preset);
    setActiveTab('edit');
  };

  const handleApplyQuickPreset = (quickPreset) => {
    setFormData({
      name: '',
      description: quickPreset.description,
      category: quickPreset.category,
      trading_hours: { ...quickPreset.trading_hours }
    });
    setSelectedPreset(null);
    setActiveTab('create');
    showToast('Quick preset applied. You can customize it before saving.', 'success');
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      showToast('Preset name is required', 'error');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (activeTab === 'create') {
        await instrumentAPI.createTradingHoursPreset(formData);
        showToast('Preset created successfully!', 'success');
      } else {
        await instrumentAPI.updateTradingHoursPreset(selectedPreset.name, formData);
        showToast('Preset updated successfully!', 'success');
      }
      
      await fetchPresets();
      setActiveTab('list');
      setSelectedPreset(null);
    } catch (err) {
      console.error('Error saving preset:', err);
      showToast(`Failed to ${activeTab === 'create' ? 'create' : 'update'} preset: ${err.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (preset) => {
    setDeleting(true);
    setError(null);
    setShowDeleteConfirm(null);

    try {
      await instrumentAPI.deleteTradingHoursPreset(preset.name);
      await fetchPresets();
      showToast(`Preset "${preset.name}" deleted`, 'success');
    } catch (err) {
      console.error('Error deleting preset:', err);
      showToast(`Failed to delete preset: ${err.message}`, 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handleDuplicate = (preset) => {
    setFormData({
      name: `${preset.name} (Copy)`,
      description: preset.description,
      category: preset.category,
      trading_hours: preset.trading_hours ? { ...preset.trading_hours } : {
        timezone: 'UTC',
        weekly_schedule: [],
        holidays: []
      }
    });
    setSelectedPreset(null);
    setActiveTab('create');
    showToast('Preset duplicated. Make your changes and save.', 'info');
  };

  const handleExport = (preset) => {
    try {
      const exportData = {
        name: preset.name,
        description: preset.description,
        category: preset.category,
        trading_hours: preset.trading_hours,
        exported_at: new Date().toISOString(),
        export_version: '1.0'
      };

      const dataStr = JSON.stringify(exportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `preset-${preset.name.replace(/[^a-z0-9]/gi, '-').toLowerCase()}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      URL.revokeObjectURL(url);
      showToast('Preset exported successfully!', 'success');
    } catch (err) {
      console.error('Error exporting preset:', err);
      showToast(`Failed to export preset: ${err.message}`, 'error');
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const text = await file.text();
      const importData = JSON.parse(text);
      
      if (!validateImportData(importData)) {
        throw new Error('Invalid preset file format');
      }

      const existingPreset = presets.find(p => p.name === importData.name);
      let finalData = { ...importData };

      if (existingPreset) {
        finalData.name = `${importData.name} (Imported)`;
      }

      finalData.trading_hours = importData.trading_hours || {
        timezone: 'UTC',
        weekly_schedule: [],
        holidays: []
      };

      await instrumentAPI.createTradingHoursPreset(finalData);
      await fetchPresets();
      showToast('Preset imported successfully!', 'success');
      event.target.value = '';
    } catch (err) {
      console.error('Error importing preset:', err);
      showToast(`Failed to import: ${err.message}`, 'error');
    }
  };

  const validateImportData = (data) => {
    if (!data || typeof data !== 'object') return false;
    if (!data.name || typeof data.name !== 'string' || !data.name.trim()) return false;
    return true;
  };

  const addSession = (dayKey) => {
    const currentSchedule = formData.trading_hours.weekly_schedule || [];
    const existingDayIndex = currentSchedule.findIndex(d => d && d.day === dayKey);
    let newSchedule;
    
    if (existingDayIndex >= 0) {
      newSchedule = [...currentSchedule];
      newSchedule[existingDayIndex] = {
        ...newSchedule[existingDayIndex],
        sessions: [
          ...newSchedule[existingDayIndex].sessions,
          { open: '09:00', close: '17:00', name: 'Trading Session' }
        ]
      };
    } else {
      newSchedule = [
        ...currentSchedule,
        {
          day: dayKey,
          sessions: [{ open: '09:00', close: '17:00', name: 'Trading Session' }]
        }
      ];
    }
    
    setFormData({
      ...formData,
      trading_hours: {
        ...formData.trading_hours,
        weekly_schedule: newSchedule
      }
    });
  };

  const removeSession = (dayKey, sessionIndex) => {
    const currentSchedule = formData.trading_hours.weekly_schedule || [];
    const existingDayIndex = currentSchedule.findIndex(d => d && d.day === dayKey);
    
    if (existingDayIndex >= 0) {
      const newSchedule = [...currentSchedule];
      const daySchedule = { ...newSchedule[existingDayIndex] };
      const sessions = [...daySchedule.sessions];
      
      sessions.splice(sessionIndex, 1);
      
      if (sessions.length === 0) {
        newSchedule.splice(existingDayIndex, 1);
      } else {
        daySchedule.sessions = sessions;
        newSchedule[existingDayIndex] = daySchedule;
      }
      
      setFormData({
        ...formData,
        trading_hours: {
          ...formData.trading_hours,
          weekly_schedule: newSchedule
        }
      });
    }
  };

  const updateSession = (dayKey, sessionIndex, field, value) => {
    const currentSchedule = formData.trading_hours.weekly_schedule || [];
    const existingDayIndex = currentSchedule.findIndex(d => d && d.day === dayKey);
    
    if (existingDayIndex >= 0) {
      const newSchedule = [...currentSchedule];
      const daySchedule = { ...newSchedule[existingDayIndex] };
      const sessions = [...daySchedule.sessions];
      
      sessions[sessionIndex] = { ...sessions[sessionIndex], [field]: value };
      daySchedule.sessions = sessions;
      newSchedule[existingDayIndex] = daySchedule;
      
      setFormData({
        ...formData,
        trading_hours: {
          ...formData.trading_hours,
          weekly_schedule: newSchedule
        }
      });
    }
  };

const toggleDay = (dayKey) => {
    if (expandedDays[dayKey]) {
      setExpandedDays(prev => ({ ...prev, [dayKey]: false }));
    } else {
      setExpandedDays(prev => ({ ...prev, [dayKey]: true }));
    }
  };

  const openDayEditor = (dayKey) => {
    const daySchedule = getDaySchedule(dayKey);
    if (!daySchedule.sessions || daySchedule.sessions.length === 0) {
      addSession(dayKey);
    }
    setExpandedDays(prev => ({ ...prev, [dayKey]: true }));
    setSelectedDayForEdit(dayKey);
  };

  const closeDayEditor = () => {
    setSelectedDayForEdit(null);
  };

  const addHoliday = () => {
    const newHolidays = [...formData.trading_hours.holidays];
    newHolidays.push({
      date: new Date().toISOString().split('T')[0],
      name: '',
      type: 'full_closure'
    });
    setFormData({
      ...formData,
      trading_hours: {
        ...formData.trading_hours,
        holidays: newHolidays
      }
    });
  };

  const removeHoliday = (index) => {
    const newHolidays = [...formData.trading_hours.holidays];
    newHolidays.splice(index, 1);
    setFormData({
      ...formData,
      trading_hours: {
        ...formData.trading_hours,
        holidays: newHolidays
      }
    });
  };

  const updateHoliday = (index, field, value) => {
    const newHolidays = [...formData.trading_hours.holidays];
    newHolidays[index] = { ...newHolidays[index], [field]: value };
    setFormData({
      ...formData,
      trading_hours: {
        ...formData.trading_hours,
        holidays: newHolidays
      }
    });
  };

  const getCategoryInfo = (category) => {
    return PRESET_CATEGORIES.find(cat => cat.value === category) || PRESET_CATEGORIES[5];
  };

  const getDaySchedule = (dayKey) => {
    return formData.trading_hours.weekly_schedule.find(s => s && s.day === dayKey) || { day: dayKey, sessions: [] };
  };

  const filteredPresets = useMemo(() => {
    return presets.filter(preset => {
      const matchesCategory = filterCategory === 'all' || preset.category === filterCategory;
      const matchesSearch = !searchQuery || 
        preset.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        preset.description?.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [presets, filterCategory, searchQuery]);

  const getPresetStats = (preset) => {
    const schedule = preset.trading_hours?.weekly_schedule || [];
    const holidays = preset.trading_hours?.holidays || [];
    const activeDays = schedule.filter(d => d.sessions && d.sessions.length > 0).length;
    const totalSessions = schedule.reduce((acc, d) => acc + (d.sessions?.length || 0), 0);
    return { activeDays, totalSessions, holidays: holidays.length };
  };

  const renderToast = () => {
    if (!toast) return null;
    const iconMap = {
      success: Check,
      error: AlertCircle,
      info: AlertCircle,
      warning: AlertCircle
    };
    const Icon = iconMap[toast.type] || AlertCircle;
    
    return (
      <div className={`pm-toast pm-toast-${toast.type}`}>
        <Icon size={18} />
        <span>{toast.message}</span>
        <button onClick={() => setToast(null)} className="pm-toast-close">
          <X size={14} />
        </button>
      </div>
    );
  };

  const renderPresetsList = () => (
    <div className="pm-list-view">
      <div className="pm-list-header">
        <div className="pm-list-header-left">
          <h3>Trading Hours Presets</h3>
          <p>{presets.length} preset{presets.length !== 1 ? 's' : ''} configured</p>
        </div>
        <div className="pm-list-header-right">
          <div className="pm-import-wrapper">
            <input
              ref={(el) => importInputRef = el}
              type="file"
              id="import-preset"
              accept=".json"
              onChange={handleImport}
              style={{ display: 'none' }}
            />
            <button 
              className="pm-btn pm-btn-secondary" 
              onClick={() => importInputRef?.click()}
            >
              <Upload size={16} />
              Import
            </button>
          </div>
          <button className="pm-btn pm-btn-primary" onClick={handleCreateNew}>
            <Plus size={16} />
            New Preset
          </button>
        </div>
      </div>

      <div className="pm-filters">
        <div className="pm-search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search presets..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="pm-search-clear">
              <X size={16} />
            </button>
          )}
        </div>
        
        <div className="pm-category-tabs">
          <button 
            className={`pm-category-tab ${filterCategory === 'all' ? 'active' : ''}`}
            onClick={() => setFilterCategory('all')}
          >
            All
          </button>
          {PRESET_CATEGORIES.map(category => (
            <button 
              key={category.value}
              className={`pm-category-tab ${filterCategory === category.value ? 'active' : ''}`}
              onClick={() => setFilterCategory(category.value)}
              style={{ '--tab-color': category.color, '--tab-bg': category.bgColor }}
            >
              {category.label}
            </button>
          ))}
        </div>

        <div className="pm-view-toggle">
          <button 
            className={`pm-view-btn ${viewMode === 'grid' ? 'active' : ''}`}
            onClick={() => setViewMode('grid')}
            title="Grid view"
          >
            <Layers size={16} />
          </button>
          <button 
            className={`pm-view-btn ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => setViewMode('list')}
            title="List view"
          >
            <Filter size={16} />
          </button>
        </div>
      </div>

      <div className={`pm-presets-${viewMode}`}>
        {filteredPresets.length > 0 ? (
          filteredPresets.map(preset => {
            const categoryInfo = getCategoryInfo(preset.category);
            const stats = getPresetStats(preset);
            return (
              <div key={preset.name} className="pm-preset-card">
                <div className="pm-preset-card-header">
                  <div 
                    className="pm-preset-icon" 
                    style={{ backgroundColor: categoryInfo.bgColor, color: categoryInfo.color }}
                  >
                    <Layers size={20} />
                  </div>
                  <div className="pm-preset-actions-menu">
                    {preset.is_system_preset && (
                      <span className="pm-system-badge">System</span>
                    )}
                    <div className="pm-dropdown">
                      <button className="pm-dropdown-trigger">
                        <MoreHorizontal size={18} />
                      </button>
                      <div className="pm-dropdown-menu">
                        <button onClick={() => handleExport(preset)}>
                          <Download size={14} /> Export
                        </button>
                        <button onClick={() => handleEdit(preset)}>
                          <Edit3 size={14} /> Edit
                        </button>
                        <button onClick={() => handleDuplicate(preset)}>
                          <Copy size={14} /> Duplicate
                        </button>
                        {!preset.is_system_preset && (
                          <button 
                            className="pm-danger" 
                            onClick={() => setShowDeleteConfirm(preset)}
                          >
                            <Trash2 size={14} /> Delete
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="pm-preset-content" onClick={() => handleEdit(preset)}>
                  <h4>{preset.name}</h4>
                  <p>{preset.description || 'No description'}</p>
                  
                  <div className="pm-preset-meta">
                    <span>
                      <Globe size={12} />
                      {preset.trading_hours?.timezone || 'UTC'}
                    </span>
                    <span>
                      <Calendar size={12} />
                      {stats.activeDays} day{stats.activeDays !== 1 ? 's' : ''}
                    </span>
                    <span>
                      <Clock size={12} />
                      {stats.totalSessions} session{stats.totalSessions !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>

                <div className="pm-preset-footer">
                  <span className="pm-preset-category" style={{ color: categoryInfo.color }}>
                    {categoryInfo.label}
                  </span>
                  <button 
                    className="pm-btn pm-btn-sm pm-btn-ghost"
                    onClick={() => handleEdit(preset)}
                  >
                    Configure <ChevronRight size={14} />
                  </button>
                </div>
              </div>
            );
          })
        ) : (
          <div className="pm-empty-state">
            <div className="pm-empty-icon">
              <CalendarDays size={48} />
            </div>
            <h4>No presets found</h4>
            <p>
              {searchQuery || filterCategory !== 'all' 
                ? 'Try adjusting your search or filters'
                : 'Create your first trading hours preset to get started'
              }
            </p>
            {!searchQuery && filterCategory === 'all' && (
              <button className="pm-btn pm-btn-primary" onClick={handleCreateNew}>
                <Plus size={16} /> Create Your First Preset
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );

  const renderPresetForm = () => (
    <div className="pm-form-view">
      <div className="pm-form-header">
        <button className="pm-back-btn" onClick={() => setActiveTab('list')}>
          <ArrowLeft size={18} /> Back to Presets
        </button>
        <h3>{activeTab === 'create' ? 'Create New Preset' : `Edit: ${selectedPreset?.name}`}</h3>
      </div>

      <div className="pm-form-content">
        <div className="pm-form-section">
          <h4 className="pm-section-title">
            <Sparkles size={18} /> Basic Information
          </h4>
          <div className="pm-form-grid">
            <div className="pm-form-group">
              <label>Preset Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Asia Forex Session"
              />
            </div>
            <div className="pm-form-group">
              <label>Category</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              >
                {PRESET_CATEGORIES.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>
            <div className="pm-form-group pm-form-group-full">
              <label>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe when to use this preset..."
                rows={2}
              />
            </div>
          </div>
        </div>

        <div className="pm-form-section">
          <h4 className="pm-section-title">
            <Globe size={18} /> General Settings
          </h4>
          <div className="pm-form-group pm-form-group-inline">
            <label>Timezone</label>
            <select
              value={formData.trading_hours.timezone}
              onChange={(e) => setFormData({
                ...formData,
                trading_hours: { ...formData.trading_hours, timezone: e.target.value }
              })}
            >
              {TIMEZONES.map(tz => (
                <option key={tz.value} value={tz.value}>
                  {tz.label} ({tz.offset})
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="pm-form-section pm-form-section-schedule">
          <div className="pm-schedule-header">
            <div>
              <h4 className="pm-section-title">
                <Calendar size={18} /> Weekly Schedule
              </h4>
              <p className="pm-section-desc">Configure trading hours for each day of the week</p>
            </div>
          </div>
          
          <div className="pm-week-schedule-container">
            <div className="pm-week-grid">
              {DAYS_OF_WEEK.map(day => {
                const daySchedule = getDaySchedule(day.key);
                const hasSessions = daySchedule.sessions && daySchedule.sessions.length > 0;
                
                return (
                  <div 
                    key={day.key} 
                    className={`pm-day-tile ${hasSessions ? 'active' : ''}`}
                    onClick={() => openDayEditor(day.key)}
                  >
                    <div className="pm-day-tile-header">
                      <span className="pm-day-short">{day.short}</span>
                      <span className="pm-day-full">{day.label}</span>
                    </div>
                    <div className="pm-day-tile-content">
                      {hasSessions ? (
                        <>
                          <div className="pm-day-sessions-preview">
                            {daySchedule.sessions.slice(0, 2).map((session, idx) => (
                              <div key={idx} className="pm-session-preview">
                                <Clock size={10} />
                                <span>{session.open?.substring(0, 5)} - {session.close?.substring(0, 5)}</span>
                              </div>
                            ))}
                            {daySchedule.sessions.length > 2 && (
                              <span className="pm-more-sessions">+{daySchedule.sessions.length - 2} more</span>
                            )}
                          </div>
                          <div className="pm-day-toggle-hint">
                            Click to edit
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="pm-day-closed">
                            <span>Closed</span>
                          </div>
                          <div className="pm-day-toggle-hint">
                            Click to enable
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {selectedDayForEdit && (
          <div className="pm-day-modal-overlay" onClick={closeDayEditor}>
            <div className="pm-day-modal" onClick={(e) => e.stopPropagation()}>
              <div className="pm-day-modal-header">
                <div className="pm-day-modal-title">
                  <Calendar size={20} />
                  <h3>{DAYS_OF_WEEK.find(d => d.key === selectedDayForEdit)?.label} Trading Hours</h3>
                </div>
                <button className="pm-day-modal-close" onClick={closeDayEditor}>
                  <X size={20} />
                </button>
              </div>
              
              <div className="pm-day-modal-content">
                {(() => {
                  const daySchedule = getDaySchedule(selectedDayForEdit);
                  return (
                    <div className="pm-modal-sessions">
                      <div className="pm-modal-sessions-header">
                        <span>Sessions ({daySchedule.sessions?.length || 0})</span>
                        <button 
                          className="pm-btn pm-btn-sm pm-btn-primary"
                          onClick={() => addSession(selectedDayForEdit)}
                        >
                          <Plus size={12} /> Add Session
                        </button>
                      </div>
                      
                      {daySchedule.sessions && daySchedule.sessions.length > 0 ? (
                        <div className="pm-modal-sessions-list">
                          {daySchedule.sessions.map((session, idx) => (
                            <div key={idx} className="pm-modal-session-item">
                              <div className="pm-modal-session-header">
                                <span className="pm-session-badge">Session {idx + 1}</span>
                                <button 
                                  className="pm-remove-session"
                                  onClick={() => removeSession(selectedDayForEdit, idx)}
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                              <div className="pm-modal-session-form">
                                <div className="pm-form-group">
                                  <label>Session Name</label>
                                  <input
                                    type="text"
                                    value={session.name || ''}
                                    onChange={(e) => updateSession(selectedDayForEdit, idx, 'name', e.target.value)}
                                    placeholder="e.g., Regular Trading, Asian Session"
                                  />
                                </div>
                                <div className="pm-modal-session-times">
                                  <div className="pm-form-group">
                                    <label>Open Time</label>
                                    <input
                                      type="time"
                                      value={session.open || ''}
                                      onChange={(e) => updateSession(selectedDayForEdit, idx, 'open', e.target.value)}
                                    />
                                  </div>
                                  <div className="pm-time-arrow">
                                    <ChevronRight size={20} />
                                  </div>
                                  <div className="pm-form-group">
                                    <label>Close Time</label>
                                    <input
                                      type="time"
                                      value={session.close || ''}
                                      onChange={(e) => updateSession(selectedDayForEdit, idx, 'close', e.target.value)}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="pm-modal-empty">
                          <div className="pm-modal-empty-icon">
                            <Clock size={32} />
                          </div>
                          <p>No trading sessions configured for this day</p>
                          <button 
                            className="pm-btn pm-btn-primary"
                            onClick={() => addSession(selectedDayForEdit)}
                          >
                            <Plus size={14} /> Enable Trading
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
              
              <div className="pm-day-modal-footer">
                <button className="pm-btn pm-btn-secondary" onClick={closeDayEditor}>
                  Done
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="pm-form-section">
          <h4 className="pm-section-title">
            <CalendarClock size={18} /> Holidays & Special Days
          </h4>
          <p className="pm-section-desc">Configure market closures for holidays and special events</p>
          
          <div className="pm-holidays">
            {formData.trading_hours.holidays && formData.trading_hours.holidays.length > 0 ? (
              <div className="pm-holidays-list">
                {formData.trading_hours.holidays.map((holiday, idx) => (
                  <div key={idx} className="pm-holiday-item">
                    <div className="pm-holiday-main">
                      <div className="pm-holiday-inputs">
                        <input
                          type="date"
                          value={holiday.date || ''}
                          onChange={(e) => updateHoliday(idx, 'date', e.target.value)}
                        />
                        <input
                          type="text"
                          value={holiday.name || ''}
                          onChange={(e) => updateHoliday(idx, 'name', e.target.value)}
                          placeholder="Holiday name"
                        />
                        <select
                          value={holiday.type || 'full_closure'}
                          onChange={(e) => updateHoliday(idx, 'type', e.target.value)}
                        >
                          {HOLIDAY_TYPES.map(type => (
                            <option key={type.value} value={type.value}>{type.label}</option>
                          ))}
                        </select>
                      </div>
                      <button 
                        className="pm-remove-holiday"
                        onClick={() => removeHoliday(idx)}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                    
                    {holiday.type !== 'full_closure' && (
                      <div className="pm-holiday-modifier">
                        <label>
                          {holiday.type === 'late_opening' ? 'Opening Time' : 'Closing Time'}
                        </label>
                        <input
                          type="time"
                          value={holiday.modified_time || ''}
                          onChange={(e) => updateHoliday(idx, 'modified_time', e.target.value)}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="pm-empty-holidays">
                <CalendarDays size={32} />
                <p>No holidays configured</p>
              </div>
            )}
            
            <button 
              className="pm-add-holiday"
              onClick={addHoliday}
            >
              <Plus size={14} /> Add Holiday
            </button>
          </div>
        </div>
      </div>

      <div className="pm-form-footer">
        <span className="pm-footer-hint">
          <Clock size={14} />
          All times are in {formData.trading_hours.timezone}
        </span>
        <div className="pm-footer-actions">
          <button 
            className="pm-btn pm-btn-secondary"
            onClick={() => setActiveTab('list')}
            disabled={saving}
          >
            Cancel
          </button>
          <button 
            className="pm-btn pm-btn-primary"
            onClick={handleSave}
            disabled={saving || !formData.name.trim()}
          >
            {saving ? (
              <>
                <Loader2 size={16} className="pm-spin" /> Saving...
              </>
            ) : (
              <>
                <Check size={16} />
                {activeTab === 'create' ? 'Create Preset' : 'Update Preset'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  const renderQuickPresets = () => (
    <div className="pm-quick-presets">
      <h4>Quick Start Templates</h4>
      <p>Start with a pre-configured template and customize it</p>
      <div className="pm-quick-presets-grid">
        {QUICK_PRESETS.map((preset, idx) => (
          <button 
            key={idx} 
            className="pm-quick-preset-card"
            onClick={() => handleApplyQuickPreset(preset)}
          >
            <div className="pm-quick-preset-icon">
              <FileJson size={24} />
            </div>
            <div className="pm-quick-preset-info">
              <h5>{preset.name}</h5>
              <p>{preset.description}</p>
            </div>
            <ChevronRight size={18} />
          </button>
        ))}
      </div>
    </div>
  );

  const renderDeleteConfirm = () => {
    if (!showDeleteConfirm) return null;
    return (
      <div className="pm-confirm-overlay">
        <div className="pm-confirm-modal">
          <div className="pm-confirm-icon">
            <AlertCircle size={32} />
          </div>
          <h4>Delete Preset?</h4>
          <p>Are you sure you want to delete "{showDeleteConfirm.name}"? This action cannot be undone.</p>
          <div className="pm-confirm-actions">
            <button 
              className="pm-btn pm-btn-secondary"
              onClick={() => setShowDeleteConfirm(null)}
              disabled={deleting}
            >
              Cancel
            </button>
            <button 
              className="pm-btn pm-btn-danger"
              onClick={() => handleDelete(showDeleteConfirm)}
              disabled={deleting}
            >
              {deleting ? <Loader2 size={16} className="pm-spin" /> : <Trash2 size={16} />}
              Delete
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="pm-overlay">
      <div className="pm-modal">
        <div className="pm-header">
          <div className="pm-header-left">
            <div className="pm-header-icon">
              <CalendarClock size={24} />
            </div>
            <div className="pm-header-text">
              <h2>Preset Manager</h2>
              <p>Trading Hours Templates</p>
            </div>
          </div>
          <button className="pm-close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="pm-tabs">
          <button 
            className={`pm-tab ${activeTab === 'list' ? 'active' : ''}`}
            onClick={() => setActiveTab('list')}
          >
            <Layers size={16} /> All Presets
          </button>
          <button 
            className={`pm-tab ${activeTab === 'create' || activeTab === 'edit' ? 'active' : ''}`}
            onClick={() => activeTab === 'list' && handleCreateNew()}
            disabled={activeTab === 'create' || activeTab === 'edit'}
          >
            <Plus size={16} /> {activeTab === 'create' ? 'Create New' : 'Edit Preset'}
          </button>
        </div>

        <div className="pm-content">
          {loading ? (
            <div className="pm-loading">
              <Loader2 size={32} className="pm-spin" />
              <p>Loading presets...</p>
            </div>
          ) : (
            <>
              {error && (
                <div className="pm-error">
                  <AlertCircle size={18} />
                  <span>{error}</span>
                  <button onClick={() => setError(null)}><X size={16} /></button>
                </div>
              )}

              {activeTab === 'list' && (
                <>
                  {renderQuickPresets()}
                  {renderPresetsList()}
                </>
              )}
              {(activeTab === 'create' || activeTab === 'edit') && renderPresetForm()}
            </>
          )}
        </div>

        {renderToast()}
        {renderDeleteConfirm()}
      </div>
    </div>
  );
}

export default PresetManager;